// src/pages/ContactsHubPage.tsx
//
// ONE Contacts destination containing People and Companies.
//
// WHY THIS EXISTS
//   Contacts and Companies were two top-level sidebar entries over the same
//   relationship area, and the split was visible to users as an inconsistency:
//   a contact showed "Company: Vanjure" while the Companies destination
//   honestly reported zero companies. That underlying data defect is fixed
//   where it was caused (the contact create form and `createContact`); this
//   shell is the information-architecture half — a person and an organization
//   are found in the same place because they are the same subject.
//
// WHAT THIS IS NOT
//   It is NOT a merge of the two domain models. A Contact is a person, a
//   Company is an organization, they remain separate records with separate
//   capabilities and separate APIs. Nothing here reads or writes either one:
//   both tabs render the EXISTING pages unchanged, so there is one
//   implementation of each experience, not a copy.
//
// ROUTING — a path, not a query parameter
//   `/contacts` is People and `/contacts/companies` is Companies, so every
//   surface in this area (both lists, the contact slide-over, the company
//   detail page at `/contacts/companies/:id`) shares the `/contacts` prefix.
//   That matters because the suite shell renders its rail with a plain
//   `NavLink to={route}`, whose active state is prefix matching on the PATH
//   and ignores the query string. A `?view=companies` tab would have left
//   company detail lighting no entry at all, and it is exactly this
//   query-string blindness that made `/messages?medium=email` light both
//   "Messages" and "Emails". The old `/companies` links redirect here.
//
// AUTHORIZATION — the tabs are independent, the shell is not an allow path
//   Consolidation must not make one page visible because its sibling is.
//   `crm:contacts:read` gates People and `crm:companies:read` gates Companies,
//   each on its own; holding one renders one tab and no tab bar decoration
//   suggesting the other exists. Holding neither renders the same refusal a
//   direct URL would get. The sidebar entry appears for either capability —
//   it has to, or a Companies-only member could not reach Companies at all —
//   and that reveals a shell, never the data inside it.

import { lazy, Suspense } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import ErrorState from '../components/common/ErrorState';
import PageLoader from '../components/common/PageLoader';
import {
  useContactPermissions,
  useCompanyPermissions,
} from '../hooks/useContactCompanyPermissions';
import ContactsPage from './ContactsPage';

// Kept lazy, exactly as the router had it before this page existed. People is
// the default tab, so a member who never opens Companies should not pay for it
// in the initial bundle just because the two now share a shell.
const CompaniesPage = lazy(() => import('./CompaniesPage'));

export type ContactsHubTab = 'people' | 'companies';

/** Canonical path for each tab. The redirect targets read from here too. */
export const CONTACTS_TAB_PATHS: Record<ContactsHubTab, string> = {
  people: '/contacts',
  companies: '/contacts/companies',
};

interface Props {
  tab: ContactsHubTab;
}

export default function ContactsHubPage({ tab }: Props) {
  const navigate = useNavigate();
  // `/contacts?contactId=<id>` is the link other surfaces use to open one
  // person — Company detail's people list and the Home execution center. The
  // hub owns the URL, so it reads the parameter and hands it to the list;
  // closing the contact drops it so Back and refresh behave.
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedContactId = searchParams.get('contactId');
  const { canViewContacts, isResolved: contactsResolved } = useContactPermissions();
  const { canViewCompanies, isResolved: companiesResolved } = useCompanyPermissions();

  const tabs: ReadonlyArray<{ key: ContactsHubTab; label: string; visible: boolean }> = [
    { key: 'people', label: 'People', visible: canViewContacts },
    { key: 'companies', label: 'Companies', visible: canViewCompanies },
  ];
  const visibleTabs = tabs.filter((t) => t.visible);

  // Both hooks report `isResolved: false` until the Central context is ready,
  // and both `can*` values are false until then. Rendering the refusal during
  // that window would flash "no permission" at someone who has it, so the
  // unresolved state renders nothing rather than an answer it does not have.
  if (!contactsResolved || !companiesResolved) return null;

  if (visibleTabs.length === 0) {
    return (
      <ErrorState
        title="Contacts unavailable"
        message="You do not have permission to view contacts or companies in this workspace."
      />
    );
  }

  // Asked for a tab this member cannot read, but the other one is readable:
  // send them to the tab that exists rather than refusing the whole area.
  // `replace` keeps the unreachable tab out of the back stack.
  const requested = tabs.find((t) => t.key === tab);
  if (!requested?.visible) {
    return <Navigate to={CONTACTS_TAB_PATHS[visibleTabs[0].key]} replace />;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Contacts</h1>
        {/*
          Same tab vocabulary the Unibox header uses — underline on the active
          item, muted text on the rest. Deliberately not a new component: this
          is the third place in the app that needs exactly this control, and a
          shared abstraction is worth extracting when the fourth arrives with
          the same requirements, not before.
        */}
        <div className="mt-4 flex gap-4 border-b border-gray-200" role="tablist" aria-label="Contacts">
          {visibleTabs.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={t.key === tab}
              onClick={() => navigate(CONTACTS_TAB_PATHS[t.key])}
              className={`pb-3 -mb-px text-sm font-medium transition-colors ${
                t.key === tab
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {tab === 'people' ? (
        <ContactsPage
          initialContactId={linkedContactId}
          onContactClosed={() => {
            if (!linkedContactId) return;
            const next = new URLSearchParams(searchParams);
            next.delete('contactId');
            setSearchParams(next, { replace: true });
          }}
        />
      ) : (
        <Suspense fallback={<PageLoader />}>
          <CompaniesPage />
        </Suspense>
      )}
    </div>
  );
}
